import type { Language } from "@/lib/i18n/types";
import type { AuthTranslations } from "@/lib/i18n/types/auth";

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

type InvitationExpiryTranslations = Pick<
  AuthTranslations,
  | "invitationExpiresInOneDay"
  | "invitationExpiresInDays"
  | "invitationExpiresUnderOneDay"
  | "invitationExpiredOn"
  | "invitationExpiryUnavailable"
>;

export type InvitationExpiryInfo =
  | { state: "days"; days: number; formattedDate: string }
  | { state: "under_one_day"; formattedDate: string }
  | { state: "expired"; formattedDate: string }
  | { state: "invalid" };

interface InvitationExpiryOptions {
  now?: Date;
  timeZone?: string;
}

export function getInvitationExpiryInfo(
  expiresAt: string,
  language: Language,
  { now = new Date(), timeZone }: InvitationExpiryOptions = {}
): InvitationExpiryInfo {
  // The edge function response is not runtime-validated, so expiresAt can arrive
  // as null/undefined despite its type. `new Date(null)` is the epoch, which would
  // render "expired on 1 January 1970" instead of the unavailable fallback.
  if (typeof expiresAt !== "string" || expiresAt.trim() === "") {
    return { state: "invalid" };
  }

  const expiry = new Date(expiresAt);
  const remainingMilliseconds = expiry.getTime() - now.getTime();

  if (!Number.isFinite(remainingMilliseconds)) {
    return { state: "invalid" };
  }

  const locale = language === "et" ? "et-EE" : "en-GB";
  const formattedDate = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
    ...(timeZone ? { timeZone } : {}),
  }).format(expiry);

  if (remainingMilliseconds <= 0) {
    return { state: "expired", formattedDate };
  }

  if (remainingMilliseconds < DAY_IN_MILLISECONDS) {
    return { state: "under_one_day", formattedDate };
  }

  return {
    state: "days",
    // Floor, not ceil: "expires in N days" must mean at least N full days remain.
    days: Math.floor(remainingMilliseconds / DAY_IN_MILLISECONDS),
    formattedDate,
  };
}

export function formatInvitationExpiry(
  info: InvitationExpiryInfo,
  translations: InvitationExpiryTranslations
): string {
  if (info.state === "invalid") {
    return translations.invitationExpiryUnavailable;
  }

  if (info.state === "expired") {
    return translations.invitationExpiredOn.replace("{date}", info.formattedDate);
  }

  if (info.state === "under_one_day") {
    return translations.invitationExpiresUnderOneDay.replace("{date}", info.formattedDate);
  }

  const template =
    info.days === 1 ? translations.invitationExpiresInOneDay : translations.invitationExpiresInDays;

  return template.replace("{count}", String(info.days)).replace("{date}", info.formattedDate);
}
